import { Badge, Button, Card, CardBody, CardHeader, Select, Surface } from '@chopchop-se/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { localeSchema, type Locale, type SessionUser } from '@warehouse/shared';
import { LogOut } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { logout, updateProfileLanguage } from '../../auth/auth-api';
import { useAuthStore } from '../../auth/auth-store';
import { useConfirm } from '../../components/ConfirmDialog';
import { PageHeader } from '../../components/PageHeader';
import { SUPPORTED_LOCALES, setLanguage } from '../../i18n';
import { formatDateTime, fullName } from '../../lib/format';
import { toastSuccess } from '../../lib/notify';

/**
 * Account settings: profile, UI language and the session.
 *
 * Language is persisted to the ACCOUNT (`PATCH /api/auth/profile` →
 * `users.language`), not to localStorage, because the same value drives the
 * emails the api sends this person (ARCHITECTURE §5). The UI switches
 * optimistically so the page re-renders in the new language while the request
 * is still in flight, and rolls back if the server refuses.
 */
export function SettingsPage(): ReactNode {
  const { t, i18n } = useTranslation(['shell', 'common']);
  const user = useAuthStore((s) => s.user);
  const session = useAuthStore((s) => s.session);
  const setUser = useAuthStore((s) => s.setUser);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();

  const languageMutation = useMutation({
    mutationFn: (language: Locale) => updateProfileLanguage(language),
    onMutate: (language: Locale) => {
      const previous = i18n.language;
      setLanguage(language);
      return { previous };
    },
    onSuccess: (updated: SessionUser) => {
      setUser(updated);
      setLanguage(updated.language);
      toastSuccess(t('shell:settings.languageSaved'));
    },
    onError: (_error, _language, context) => {
      const previous = localeSchema.safeParse(context?.previous);
      if (previous.success) setLanguage(previous.data);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => logout(),
    onSettled: () => {
      // Even a failed logout must clear the client: the cookie may well be gone
      // and leaving cached rows on a shared floor terminal is worse than a
      // redundant sign-out.
      setAnonymous();
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });

  if (!user) return null;

  const languageOptions = SUPPORTED_LOCALES.map((locale) => ({
    value: locale,
    label: t(`common:language.${locale}`),
  }));

  return (
    <>
      {dialog}
      <PageHeader title={t('shell:settings.title')} subtitle={t('shell:settings.subtitle')} />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">{t('shell:settings.profileSection')}</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <Field label={t('shell:settings.name')} value={fullName(user) || '—'} />
            <Field label={t('shell:settings.email')} value={user.email} />
            <div>
              <p className="mb-1.5 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
                {t('shell:settings.roles')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {user.roleKeys.length === 0 ? (
                  <span className="text-sm text-ink-muted">—</span>
                ) : (
                  user.roleKeys.map((role) => (
                    <Badge key={role} tone={role === 'super_admin' ? 'brand' : 'neutral'}>
                      {role}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">{t('shell:settings.languageSection')}</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <Select
              label={t('common:common.language')}
              options={languageOptions}
              value={user.language}
              disabled={languageMutation.isPending}
              onChange={(event) => {
                const parsed = localeSchema.safeParse(event.target.value);
                if (parsed.success && parsed.data !== user.language) languageMutation.mutate(parsed.data);
              }}
            />
            <p className="text-sm text-ink-muted">{t('shell:settings.languageHelp')}</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">{t('shell:settings.sessionSection')}</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <Field
              label={t('shell:settings.sessionKind')}
              value={
                session?.kind === 'device' ? t('shell:settings.sessionKindDevice') : t('shell:settings.sessionKindWeb')
              }
            />
            <Field label={t('shell:settings.sessionExpires')} value={formatDateTime(session?.expiresAt)} />
            <div>
              <Button
                variant="danger"
                icon={<LogOut />}
                loading={logoutMutation.isPending}
                onClick={() => {
                  void (async () => {
                    const confirmed = await confirm({
                      title: t('shell:settings.signOutConfirmTitle'),
                      message: t('shell:settings.signOutConfirmMessage'),
                      confirmLabel: t('shell:auth.signOut'),
                    });
                    if (confirmed) logoutMutation.mutate();
                  })();
                }}
              >
                {t('shell:settings.signOutEverywhere')}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">
              {t('shell:settings.permissionsSection')}
            </h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <p className="text-sm text-ink-muted">{t('shell:settings.permissionsHelp')}</p>
            <Surface variant="subtle" className="max-h-64 overflow-y-auto p-3 scrollbar-thin">
              {user.permissions.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('shell:settings.noPermissions')}</p>
              ) : (
                <ul className="flex flex-col gap-1 font-mono text-xs text-ink-light">
                  {user.permissions.map((permission) => (
                    <li key={permission}>{permission}</li>
                  ))}
                </ul>
              )}
            </Surface>
            <p className="text-xs text-ink-muted">
              {t('shell:settings.permissionsCount', { count: user.permissions.length })}
            </p>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <p className="mb-1 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</p>
      <p className="text-sm text-ink">{value}</p>
    </div>
  );
}
